import React, { useState } from "react";
import { Alert, Input, Table, Tag, Button, Form, message } from "antd";
import { Award, Copy, Edit2, Check, Gift } from "react-feather";
import { useTranslation } from "react-i18next";
import {
  useGetReferralDashboard,
  useGetReferralCode,
  useGetRecentReferralHistory,
  useUpdateReferralCode,
} from "../../services/apis/referral.api";
import { useCopyToClipboard } from "../../utils/clipboard";
import { ReferralStatus } from "../../types/referral.type";

const ReferralPage: React.FC = () => {
  const { t } = useTranslation();
  const { copyText: copyCode, isCopied: isCodeCopied } = useCopyToClipboard();
  const { copyText: copyLink, isCopied: isLinkCopied } = useCopyToClipboard();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);

  // Fetch dashboard data (stats + active rewards)
  const { error: dashboardError, refetch: refetchDashboard } =
    useGetReferralDashboard();

  // Fetch referral code
  const {
    referralCode,
    referralLink,
    isLoading: isCodeLoading,
    error: codeError,
    refetch: refetchCode,
  } = useGetReferralCode();

  // Update referral code mutation
  const updateCodeMutation = useUpdateReferralCode();

  // Fetch recent history
  const {
    recentReferrals,
    isLoading: isHistoryLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useGetRecentReferralHistory(10);

  // Handle global error state
  const hasError = dashboardError || codeError || historyError;

  const handleRetryAll = () => {
    refetchDashboard();
    refetchCode();
    refetchHistory();
  };

  const handleUpdateCode = async (values: { referral_code: string }) => {
    try {
      await updateCodeMutation.mutateAsync({
        referral_code: values.referral_code,
      });
      setIsEditing(false);
      message.success(t("referral.updateSuccess"));
      refetchCode();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || t("referral.updateError");
      message.error(errorMessage);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    form.setFieldsValue({ referral_code: referralCode });
  };

  // Table columns for referral list
  const columns = [
    {
      title: t("referral.table.account"),
      dataIndex: "account",
      key: "account",
      render: (_: string, record: any) => (
        <span style={{ color: "var(--text-primary)" }}>
          {record.referred_user?.name ||
            record.referred_user?.id ||
            t("referral.table.guestId") + " " + record.id}
        </span>
      ),
    },
    {
      title: t("referral.table.registered"),
      dataIndex: "registered",
      key: "registered",
      align: "center" as const,
      render: (_: string, record: any) => (
        <Tag
          color={record.status !== ReferralStatus.PENDING ? "green" : "default"}
        >
          ✓
        </Tag>
      ),
    },
    {
      title: t("referral.table.createdAlbum"),
      dataIndex: "created_album",
      key: "created_album",
      align: "center" as const,
      render: (_: string, record: any) => (
        <Tag
          color={
            [
              ReferralStatus.ALBUM_CREATED,
              ReferralStatus.INTERACTED,
              ReferralStatus.SUCCESSFUL,
            ].includes(record.status)
              ? "green"
              : "default"
          }
        >
          ✓
        </Tag>
      ),
    },
    {
      title: t("referral.table.albumInteracted"),
      dataIndex: "album_interacted",
      key: "album_interacted",
      align: "center" as const,
      render: (_: string, record: any) => (
        <Tag
          color={
            [ReferralStatus.INTERACTED, ReferralStatus.SUCCESSFUL].includes(
              record.status
            )
              ? "green"
              : "default"
          }
        >
          ✓
        </Tag>
      ),
    },
    {
      title: t("referral.table.sevenDays"),
      dataIndex: "seven_days",
      key: "seven_days",
      align: "center" as const,
      render: (_: string, record: any) => (
        <Tag
          color={
            record.status === ReferralStatus.SUCCESSFUL ? "green" : "default"
          }
        >
          ✓
        </Tag>
      ),
    },
    {
      title: t("referral.table.completed"),
      dataIndex: "completed",
      key: "completed",
      align: "center" as const,
      render: (_: string, record: any) => (
        <Tag
          color={
            record.status === ReferralStatus.SUCCESSFUL ? "green" : "default"
          }
        >
          ✓
        </Tag>
      ),
    },
  ];

  return (
    <div className="referral-page">
      {/* Global Error State */}
      {hasError && (
        <div className="max-w-4xl mx-auto px-4">
          <Alert
            message={t("referral.errors.loadDataError")}
            description={t("referral.errors.loadDataErrorDescription")}
            type="error"
            showIcon
            action={
              <button
                onClick={handleRetryAll}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors"
              >
                {t("referral.errors.retry")}
              </button>
            }
            className="mb-6"
          />
        </div>
      )}

      {/* Header Component - Giới thiệu & nhận thưởng */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg mx-4 mb-6">
        <div className="max-w-4xl mx-auto py-12 px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-xl">
                <Gift className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-yellow-600 mb-4">
              {t("referral.title")}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed text-lg">
              {t("referral.subtitle")}
            </p>
          </div>

          {/* Description */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-8 mt-8">
            <p
              className="text-gray-700 dark:text-gray-300 leading-relaxed text-center"
              dangerouslySetInnerHTML={{ __html: t("referral.description") }}
            />
          </div>

          {/* Referral Code and Link */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="text-center">
              <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t("referral.yourReferralCode")}
              </label>
              {isCodeLoading ? (
                <div className="h-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
                </div>
              ) : (
                <div className="relative">
                  {isEditing ? (
                    <>
                      <Form form={form} onFinish={handleUpdateCode}>
                        <Form.Item
                          name="referral_code"
                          className="mb-0"
                          initialValue={referralCode}
                          rules={[
                            {
                              required: true,
                              message: t("referral.codeRequired"),
                            },
                            {
                              min: 3,
                              message: t("referral.codeMinLength"),
                            },
                          ]}
                        >
                          <Input
                            className="h-16 text-center font-mono text-xl border-2 border-yellow-400"
                            style={{
                              backgroundColor: "transparent",
                              color: "var(--text-primary)",
                              fontWeight: "bold",
                            }}
                          />
                        </Form.Item>
                      </Form>
                      <div className="flex justify-center gap-2 mt-3">
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={updateCodeMutation.isLoading}
                          className="bg-green-500 hover:bg-green-600 border-green-500"
                          onClick={() => form.submit()}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {t("referral.save")}
                        </Button>
                        <Button onClick={handleCancelEdit}>
                          {t("referral.cancel")}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Input
                        value={referralCode || ""}
                        readOnly
                        placeholder={!referralCode ? t("referral.loading") : ""}
                        className="h-16 text-center font-mono text-xl border-2 border-dashed border-gray-400 dark:border-gray-500"
                        style={{
                          backgroundColor: "transparent",
                          color: "var(--text-primary)",
                          fontWeight: "bold",
                        }}
                      />
                      <div className="flex justify-center gap-2 mt-3">
                        <Button
                          type="primary"
                          icon={<Copy className="w-4 h-4" />}
                          onClick={() => referralCode && copyCode(referralCode)}
                          className="bg-blue-500 hover:bg-blue-600 border-blue-500"
                        >
                          {isCodeCopied
                            ? t("referral.copied")
                            : t("referral.copyCode")}
                        </Button>
                        <Button
                          icon={<Edit2 className="w-4 h-4" />}
                          onClick={() => setIsEditing(true)}
                          className="border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                        >
                          {t("referral.editCode")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="text-center">
              <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t("referral.orReferralLink")}
              </label>
              {isCodeLoading ? (
                <div className="h-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    value={referralLink || ""}
                    readOnly
                    placeholder={!referralLink ? t("referral.loading") : ""}
                    className="h-16 text-center text-sm border-2 border-dashed border-gray-400 dark:border-gray-500"
                    style={{
                      backgroundColor: "transparent",
                      color: "var(--text-primary)",
                    }}
                  />
                  <div className="flex justify-center mt-3">
                    <Button
                      type="primary"
                      icon={<Copy className="w-4 h-4" />}
                      onClick={() => referralLink && copyLink(referralLink)}
                      className="bg-blue-500 hover:bg-blue-600 border-blue-500"
                    >
                      {isLinkCopied
                        ? t("referral.copied")
                        : t("referral.copyLink")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rewards Component - PHẦN THƯỞNG CỦA BẠN */}
      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 border border-yellow-300 rounded-2xl shadow-xl mx-4 mb-6">
        <div className="max-w-4xl mx-auto py-6 px-6 text-black">
          <div className="text-sm leading-relaxed">
            <strong>{t("referral.rewardsTitle")}</strong>{" "}
            {t("referral.rewardsDescription")}
          </div>
          <div className="mt-3 space-y-1 text-sm leading-relaxed">
            <div>{t("referral.reward1")}</div>
            <div>{t("referral.reward2")}</div>
            <div>{t("referral.reward3")}</div>
            <div>{t("referral.reward4")}</div>
          </div>
        </div>
      </div>

      {/* Table Component - Danh sách bạn bè đã mời */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg mx-4 mb-8">
        <div className="max-w-4xl mx-auto py-8 px-8">
          <h3 className="text-2xl font-bold text-yellow-600 mb-8 text-center">
            {t("referral.friendsListTitle")}
          </h3>
          <div className="overflow-hidden">
            <Table
              columns={columns}
              dataSource={recentReferrals || []}
              loading={isHistoryLoading}
              pagination={false}
              rowKey="id"
              className="referral-table"
              size="large"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;
